import React, { useEffect, useState } from "react";
import { Save, RotateCcw, History as HistoryIcon, Sparkles, Loader2, CheckCircle2 } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT");
  } catch {
    return iso;
  }
};

export default function SuperAdminAiConfig() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState("");
  const [originalPrompt, setOriginalPrompt] = useState("");
  const [model, setModel] = useState("");
  const [originalModel, setOriginalModel] = useState("");
  const [version, setVersion] = useState(0);
  const [updatedAt, setUpdatedAt] = useState("");
  const [updatedBy, setUpdatedBy] = useState("");
  const [historyItems, setHistoryItems] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ai/config");
      setSystemPrompt(data.system_prompt || "");
      setOriginalPrompt(data.system_prompt || "");
      setModel(data.model || "");
      setOriginalModel(data.model || "");
      setVersion(data.version || 0);
      setUpdatedAt(data.updated_at || "");
      setUpdatedBy(data.updated_by || "");
    } catch (e) {
      toast.error(`Errore caricamento config: ${e?.response?.data?.detail || e?.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/ai/config/history");
      setHistoryItems(data.items || []);
    } catch (e) {
      toast.error(`Errore caricamento storico: ${e?.response?.data?.detail || e?.message}`);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isDirty = systemPrompt !== originalPrompt || model !== originalModel;

  const onSave = async () => {
    if (!isDirty) return;
    if (systemPrompt.trim().length < 10) {
      toast.error("Il system prompt deve contenere almeno 10 caratteri.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/ai/config", { system_prompt: systemPrompt, model });
      setSystemPrompt(data.system_prompt || "");
      setOriginalPrompt(data.system_prompt || "");
      setModel(data.model || "");
      setOriginalModel(data.model || "");
      setVersion(data.version || 0);
      setUpdatedAt(data.updated_at || "");
      setUpdatedBy(data.updated_by || "");
      toast.success(`Configurazione salvata (v${data.version})`);
    } catch (e) {
      toast.error(`Errore salvataggio: ${e?.response?.data?.detail || e?.message}`);
    } finally {
      setSaving(false);
    }
  };

  const onRevert = () => {
    setSystemPrompt(originalPrompt);
    setModel(originalModel);
    toast.success("Modifiche non salvate scartate.");
  };

  const onShowHistory = async () => {
    if (!showHistory) await loadHistory();
    setShowHistory((s) => !s);
  };

  const onRestoreVersion = (entry) => {
    if (!entry?.system_prompt) return;
    setSystemPrompt(entry.system_prompt);
    if (entry.model) setModel(entry.model);
    toast.info(`Versione v${entry.version} caricata. Clicca Salva per applicare.`);
  };

  if (loading) {
    return (
      <div className="px-8 py-12 flex items-center justify-center text-text-secondary">
        <Loader2 className="animate-spin mr-2" size={18} /> Caricamento configurazione…
      </div>
    );
  }

  return (
    <div className="px-8 py-8 max-w-5xl" data-testid="ai-config-page">
      <div className="mb-6">
        <div className="text-[11px] font-medium uppercase tracking-wider text-text-muted mb-1">Wattline · Assistente AI</div>
        <h1 className="text-3xl font-medium tracking-tight text-text-primary flex items-center gap-2">
          <Sparkles size={22} className="text-accent" />
          Configurazione Assistente AI
        </h1>
        <p className="text-sm text-text-secondary mt-2 max-w-2xl">
          System prompt e modello in vigore per <strong>tutte le conversazioni</strong> dell&apos;assistente.
          Le modifiche entrano in vigore al messaggio successivo. Lo storico delle versioni è conservato per il rollback.
        </p>
      </div>

      {/* Metadata corrente */}
      <div className="bg-surface border border-border rounded-xl p-5 mb-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-text-muted">Versione</div>
          <div className="text-text-primary font-medium mt-0.5" data-testid="cfg-version">v{version}</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-text-muted">Modello</div>
          <div className="text-text-primary font-mono text-xs mt-0.5" data-testid="cfg-model-current">{originalModel || "—"}</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-text-muted">Aggiornato</div>
          <div className="text-text-primary mt-0.5">{fmtDate(updatedAt)}</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-text-muted">Da</div>
          <div className="text-text-primary mt-0.5 truncate">{updatedBy || "—"}</div>
        </div>
      </div>

      {/* Modello */}
      <div className="mb-5 space-y-1.5">
        <Label className="text-xs font-medium text-text-secondary">Modello AI</Label>
        <Input
          type="text"
          value={model}
          onChange={(e) => setModel(e.target.value)}
          placeholder="claude-sonnet-4-6"
          data-testid="cfg-model"
          className="h-11 rounded-md border-border bg-surface font-mono text-sm focus-visible:ring-accent/30 focus-visible:border-accent"
        />
        <p className="text-xs text-text-muted">
          Modelli Anthropic disponibili: <span className="font-mono">claude-sonnet-4-6</span> (consigliato),
          {" "}<span className="font-mono">claude-sonnet-4-5-20250929</span>,
          {" "}<span className="font-mono">claude-haiku-4-5-20251001</span>,
          {" "}<span className="font-mono">claude-opus-4-7</span> (più costoso, top qualità).
        </p>
      </div>

      {/* System prompt */}
      <div className="mb-5 space-y-1.5">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-medium text-text-secondary">System prompt</Label>
          <span className="text-[11px] text-text-muted">{systemPrompt.length.toLocaleString("it-IT")} caratteri</span>
        </div>
        <textarea
          value={systemPrompt}
          onChange={(e) => setSystemPrompt(e.target.value)}
          rows={28}
          data-testid="cfg-system-prompt"
          className="w-full rounded-md border border-border bg-surface px-4 py-3 text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
          spellCheck={false}
        />
        <p className="text-xs text-text-muted">
          Markdown supportato. Le modifiche sono registrate con autore e timestamp. Lo storico ti permette di tornare indietro.
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 mb-6">
        <Button
          onClick={onSave}
          disabled={!isDirty || saving}
          data-testid="cfg-save"
          className="bg-brand hover:bg-brand-hover text-white"
        >
          {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
          {saving ? "Salvataggio…" : "Salva configurazione"}
        </Button>
        <Button
          onClick={onRevert}
          disabled={!isDirty || saving}
          variant="outline"
          data-testid="cfg-revert"
        >
          <RotateCcw size={14} className="mr-2" />
          Annulla modifiche
        </Button>
        <Button
          onClick={onShowHistory}
          variant="ghost"
          data-testid="cfg-history-toggle"
          className="ml-auto"
        >
          <HistoryIcon size={14} className="mr-2" />
          {showHistory ? "Nascondi storico" : "Storico versioni"}
        </Button>
      </div>

      {isDirty && (
        <div className="rounded-md bg-warning-bg border border-warning text-warning-strong text-sm px-4 py-2.5 mb-6 inline-flex items-center gap-2">
          <span>Hai modifiche non salvate.</span>
        </div>
      )}
      {!isDirty && (
        <div className="text-xs text-text-muted flex items-center gap-1.5 mb-6">
          <CheckCircle2 size={12} className="text-success" />
          Configurazione attuale salvata.
        </div>
      )}

      {/* History */}
      {showHistory && (
        <div className="bg-surface border border-border rounded-xl overflow-hidden mb-6" data-testid="cfg-history">
          <div className="px-5 py-3 border-b border-border bg-subtle text-sm font-medium text-text-primary">
            Storico versioni (ultime 20)
          </div>
          {historyItems.length === 0 ? (
            <div className="px-5 py-6 text-sm text-text-muted text-center">Nessuna versione precedente registrata.</div>
          ) : (
            <ul className="divide-y divide-border">
              {historyItems.map((it, i) => (
                <li key={i} className="px-5 py-4 flex items-center justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-text-primary font-medium">v{it.version} · {fmtDate(it.saved_at)}</div>
                    <div className="text-xs text-text-secondary truncate">
                      Da: {it.saved_by || "—"} · Modello: <span className="font-mono">{it.model || "—"}</span> ·
                      {" "}{(it.system_prompt || "").length.toLocaleString("it-IT")} caratteri
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onRestoreVersion(it)}
                    data-testid={`cfg-restore-v${it.version}`}
                    className="text-xs px-3 py-1.5 rounded-md border border-border-strong bg-surface hover:bg-subtle text-text-primary"
                  >
                    Carica nel form
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
